import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";
import SocialLinks from "@/components/SocialLinks";
import { getYouTubeId } from "@/lib/videoUrl";

export default function Contact() {
  const { t, pick } = useLang();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const initType = params.get("type") || "general";
  const vehicleId = params.get("vehicle") || null;

  const [site, setSite] = useState({});
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", message: "",
    request_type: initType, vehicle_id: vehicleId,
    preferred_date: "", preferred_slot: "",
  });

  useEffect(() => { api.get("/site").then((r) => setSite(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", request_type: initType, vehicle_id: vehicleId, preferred_date: "", preferred_slot: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const heroYtId = getYouTubeId(site.hero_video_url);
  const wa = site.whatsapp ? `https://wa.me/${site.whatsapp.replace(/[^\d]/g, "")}` : null;
  const tg = site.telegram ? `https://t.me/${site.telegram.replace(/^@/, "").trim()}` : null;

  return (
    <div data-testid="contact-page" className="bg-[#0D0D0D] min-h-screen">
      <SEO title="Contact · Prendre rendez-vous" description="Contactez KV International — Chaussée de Waterloo 242, 1640 Rhode-Saint-Genèse. Uniquement sur rendez-vous. WhatsApp, téléphone, email, formulaire." />
      {/* Video hero band */}
      {site.hero_video_url && (
        <section className="relative h-[42vh] min-h-[300px] overflow-hidden">
          {heroYtId ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${heroYtId}?autoplay=1&mute=1&loop=1&playlist=${heroYtId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3`}
              title="Vidéo showroom KV International"
              allow="autoplay; encrypted-media"
              data-testid="contact-hero-video"
              className="absolute top-1/2 left-1/2 w-[max(100%,177.78vh)] h-[max(100%,56.25vw)] -translate-x-1/2 -translate-y-1/2 border-0 pointer-events-none"
            />
          ) : (
            <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover img-fade" data-testid="contact-hero-video">
              <source src={site.hero_video_url} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/40 via-[#0D0D0D]/60 to-[#0D0D0D]" />
          <div className="absolute inset-x-0 bottom-0 max-w-[1400px] mx-auto px-6 lg:px-10 pb-10">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#C8A75A]">Showroom · Rhode-Saint-Genèse</p>
          </div>
        </section>
      )}
      <div className={`${site.hero_video_url ? "pt-16" : "pt-32"} pb-24`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">05 — Contact</p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light text-white">{t.contact.title}</h1>
        <p className="text-white/60 text-lg mt-4 max-w-2xl">{t.contact.subtitle}</p>
        <p className="text-white text-base mt-6 max-w-2xl">
          <span className="text-[#C8A75A] uppercase tracking-widest text-xs mr-2">Showroom d'exposition</span>
          Chaussée de Waterloo 242 · 1640 Rhode-Saint-Genèse
        </p>

        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 mt-16">
          {/* Info */}
          <div className="space-y-6">
            <InfoRow icon={MapPin} label={t.contact.address} value={site.address} />
            <InfoRow icon={Phone} label="Téléphone" value={site.phone} href={site.phone ? `tel:${site.phone}` : null} />
            {wa && <InfoRow icon={MessageCircle} label={t.contact.whatsapp} value={site.whatsapp} href={wa} external />}
            {tg && <InfoRow icon={Send} label="Telegram" value={`@${site.telegram.replace(/^@/, "")}`} href={tg} external />}
            <InfoRow icon={Mail} label="Email" value={site.email} href={site.email ? `mailto:${site.email}` : null} />
            <InfoRow icon={Clock} label={t.contact.hours} value={pick(site.hours)} />

            <SocialLinks variant="cta" />

            <div className="aspect-[4/3] rounded-md overflow-hidden border border-white/10 mt-4">
              <iframe
                data-testid="map-embed"
                src={site.map_embed || "https://www.google.com/maps?q=Chauss%C3%A9e+de+Waterloo+242,+1640+Rhode-Saint-Gen%C3%A8se&output=embed"}
                width="100%" height="100%" style={{ border: 0, filter: "grayscale(0.6) invert(0.9) contrast(0.85)" }}
                loading="lazy" title="map" />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} data-testid="contact-form" className="bg-[#1E1E1E] border border-white/10 rounded-md p-8">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label={t.contact.name} required>
                <Input data-testid="cf-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="bg-[#0D0D0D] border-white/10 text-white h-11" />
              </Field>
              <Field label={t.contact.email} required>
                <Input data-testid="cf-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="bg-[#0D0D0D] border-white/10 text-white h-11" />
              </Field>
              <Field label={t.contact.phone}>
                <Input data-testid="cf-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
              </Field>
              <Field label={t.contact.request_type}>
                <Select value={form.request_type} onValueChange={(v) => set("request_type", v)}>
                  <SelectTrigger data-testid="cf-type" className="bg-[#0D0D0D] border-white/10 text-white h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                    {Object.entries(t.contact.types).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label={t.contact.subject}>
              <Input data-testid="cf-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
            </Field>

            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A] mb-3">Créneau souhaité (optionnel)</p>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Date">
                  <Input
                    data-testid="cf-date"
                    type="date"
                    value={form.preferred_date}
                    onChange={(e) => set("preferred_date", e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    className="bg-[#0D0D0D] border-white/10 text-white h-11"
                  />
                </Field>
                <Field label="Créneau">
                  <Select value={form.preferred_slot || "any"} onValueChange={(v) => set("preferred_slot", v === "any" ? "" : v)}>
                    <SelectTrigger data-testid="cf-slot" className="bg-[#0D0D0D] border-white/10 text-white h-11">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                      <SelectItem value="any">Peu importe</SelectItem>
                      <SelectItem value="morning">Matin (9h-12h)</SelectItem>
                      <SelectItem value="afternoon">Après-midi (13h-17h)</SelectItem>
                      <SelectItem value="evening">Soir (17h-20h)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>

            <Field label={t.contact.message} required>
              <Textarea data-testid="cf-message" value={form.message} onChange={(e) => set("message", e.target.value)} required rows={6} className="bg-[#0D0D0D] border-white/10 text-white" />
            </Field>

            <Button type="submit" disabled={sending} data-testid="cf-submit" className="mt-6 bg-[#C8A75A] text-black hover:bg-white uppercase tracking-widest text-xs px-8 py-6 rounded-md w-full md:w-auto">
              {sending ? "…" : t.contact.send}
            </Button>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="mt-4">
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">
        {label}{required && <span className="text-[#C8A75A]"> *</span>}
      </label>
      {children}
    </div>
  );
}
function InfoRow({ icon: Icon, label, value, href, external }) {
  if (!value) return null;
  const content = (
    <div className="flex gap-4 items-start bg-[#1E1E1E] border border-white/10 rounded-md p-5 hover:border-[#C8A75A]/40 transition-colors">
      <Icon className="w-5 h-5 text-[#C8A75A] mt-0.5 flex-shrink-0" strokeWidth={1.3} />
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</p>
        <p className="text-white text-sm mt-1">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href} target={external ? "_blank" : undefined} rel="noreferrer" className="block">{content}</a> : content;
}
